import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseComponent } from '@macs-frontend/core';
import { toAppTime } from '@macs-frontend/core/utils/time';
import { action, makeObservable } from 'mobx';
import { observable } from 'mobx-angular';

@Component({
  selector: 'md-time',
  templateUrl: './time.component.html',
  styleUrls: ['./time.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeComponent),
      multi: true,
    },
  ],
})
export class TimeComponent
  extends BaseComponent
  implements OnInit, ControlValueAccessor, OnDestroy, OnChanges
{
  @Input()
  inputId?: string;
  @observable
  inputId_?: string = undefined;

  @Input()
  inputDisabled: boolean | null = false;
  @observable
  inputDisabled_ = false;

  @Input()
  showTimeTooltip: boolean | null = true;
  @observable
  showTimeTooltip_ = false;

  @Input()
  timeTooltipContent!: TemplateRef<any>;
  @observable.ref
  timeTooltipContent_: TemplateRef<any> = undefined!;

  @Input()
  inputClasses:
    | string
    | string[]
    | Set<string>
    | {
        [klass: string]: any;
      } = '';

  @Output() inputValue: EventEmitter<string> = new EventEmitter();

  @observable
  inputClasses_:
    | string
    | string[]
    | Set<string>
    | {
        [klass: string]: any;
      } = '';

  @Output()
  dontShowTimeTooltipAgain = new EventEmitter<boolean>();

  @Output()
  inputBlur = new EventEmitter<string>();

  @Output()
  enter = new EventEmitter<string>();

  @Output()
  valueChange = new EventEmitter<string>();

  @observable
  isTooltipOpen = false;

  @observable
  modelValue = '';

  onChangeCallback = (_: string) => {};

  @action
  inputBlurHandle = () => {
    this.inputBlur.emit(this.modelValue);
  };

  @action
  enterHandle = () => {
    this.enter.emit(this.modelValue);
  };

  ngOnInit(): void {}

  @action
  toggleTooltip(forceValue = !this.isTooltipOpen) {
    this.isTooltipOpen = this.showTimeTooltip_ && forceValue;
  }

  handleDontShowTimeTooltipAgain() {
    this.dontShowTimeTooltipAgain.emit(true);
    this.toggleTooltip(false);
  }

  @HostListener('click') onClick() {
    this.toggleTooltip(true);
  }

  @HostListener('document:click', ['$event.target'])
  public onDocumentClick(element: HTMLElement) {
    const insideClick = this.elementRef.nativeElement.contains(element);
    if (!insideClick) {
      this.toggleTooltip(false);
    }
  }

  constructor(private elementRef: ElementRef) {
    super();
    makeObservable(this);
  }

  @action
  ngOnChanges(changes: SimpleChanges): void {
    if ('inputId' in changes) {
      this.inputId_ = changes.inputId.currentValue;
    }

    if ('inputDisabled' in changes) {
      this.inputDisabled_ = !!changes.inputDisabled.currentValue;
    }

    if ('showTimeTooltip' in changes) {
      this.showTimeTooltip_ = !!changes.showTimeTooltip.currentValue;
    }

    if ('timeTooltipContent' in changes) {
      this.timeTooltipContent_ = changes.timeTooltipContent.currentValue;
    }

    if ('inputClasses' in changes) {
      this.inputClasses_ = changes.inputClasses.currentValue;
    }
  }

  ngOnDestroy(): void {}

  @action
  writeValue(value: null | string | number) {
    if (typeof value === 'number') {
      // eslint-disable-next-line no-param-reassign
      value = toAppTime(value, false, false);
    }

    if (value === '00:00') {
      // eslint-disable-next-line no-param-reassign
      value = '';
    }

    this.modelValue = value || '';
    this.onChangeCallback(this.modelValue);
    this.valueChange.emit(this.modelValue);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChangeCallback = action(fn);
  }

  registerOnTouched(fn: any): void {}

  changeInputValue(event: any) {
    this.inputValue.emit(event.target.value);
  }
}
